/**
 * SocialTabbedPanel - Panel izquierdo con pestañas para módulo social
 *
 * @version 1.0
 * @module core/modules/social
 *
 * @description
 * Pestañas: Escuelas, Hospitales, Centros comunitarios, Deportivos, Culturales, Asistenciales
 */

import { useState } from 'react';
import {
  GraduationCap, HeartPulse, Users, Dumbbell, Theater, HandHeart,
} from 'lucide-react';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useSocialElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { SocialElementId } from './constants/element-colors';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type SocialTabType =
  | 'schools'
  | 'hospitals'
  | 'community'
  | 'sports'
  | 'cultural'
  | 'welfare';

interface TabInfo {
  id: SocialTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'schools',    icon: <GraduationCap size={16} />, tooltip: 'Escuelas',              title: 'Escuelas',             emptyMessage: 'Sin escuelas registradas' },
  { id: 'hospitals',  icon: <HeartPulse size={16} />,    tooltip: 'Hospitales y centros de salud', title: 'Centros de salud', emptyMessage: 'Sin centros de salud' },
  { id: 'community',  icon: <Users size={16} />,         tooltip: 'Centros comunitarios',   title: 'Centros comunitarios', emptyMessage: 'Sin centros comunitarios' },
  { id: 'sports',     icon: <Dumbbell size={16} />,      tooltip: 'Centros deportivos',     title: 'Centros deportivos',   emptyMessage: 'Sin centros deportivos' },
  { id: 'cultural',   icon: <Theater size={16} />,       tooltip: 'Espacios culturales',    title: 'Espacios culturales',  emptyMessage: 'Sin espacios culturales' },
  { id: 'welfare',    icon: <HandHeart size={16} />,     tooltip: 'Centros asistenciales',  title: 'Centros asistenciales', emptyMessage: 'Sin centros asistenciales' },
];

export function SocialTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<SocialTabType>('schools');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useSocialElementColorsStore((s) => s.getColor);
  const setColor = useSocialElementColorsStore((s) => s.setColor);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `social-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log(`[SocialTabbedPanel] Elemento clickeado: ${activeTab} - ${elementId}`);
    const canChange = await canChangeContent();
    if (!canChange) return;

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[SocialTabbedPanel] Despachando evento: social-${activeTab}-select`);
    window.dispatchEvent(new CustomEvent(`social-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            <BaseInventoryTab
              key={activeTab}
              title={activeTabInfo.title}
              emptyMessage={activeTabInfo.emptyMessage}
              eventPrefix={`social-${activeTab}`}
              moduleId="social"
              elementColor={isMappable ? getColor(activeTab as SocialElementId) : undefined}
              onElementColorChange={isMappable ? (hex) => setColor(activeTab as SocialElementId, hex) : undefined}
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
